import { useState, useCallback, useEffect, useRef } from "react";
import { apiClient, apiErrorMessage } from "@/api/client";

interface PaginatedResponse<T> {
  items: T[];
  nextCursor?: string | null;
}

interface UsePaginatedQueryResult<T> {
  items: T[];
  isLoading: boolean;
  error: string | null;
  hasNext: boolean;
  hasPrev: boolean;
  goNext: () => void;
  goPrev: () => void;
  refresh: (force?: boolean) => void;
  requestUrl?: string;
}

export function usePaginatedQuery<T>(
  path: string,
  params?: Record<string, string | undefined>,
  options?: {
    cursor?: string;
    onCursorChange?: (cursor: string | undefined) => void;
    fetcher?: (url: string, force?: boolean) => Promise<PaginatedResponse<T>>;
  },
): UsePaginatedQueryResult<T> {
  const [items, setItems] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [currentCursor, setCurrentCursor] = useState<string | undefined>();
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const requestSequence = useRef(0);
  const baseQueryString = new URLSearchParams(
    Object.entries(params ?? {})
      .filter((entry): entry is [string, string] => entry[1] !== undefined)
      .sort(([left], [right]) => left.localeCompare(right)),
  ).toString();

  const buildUrl = useCallback(() => {
    const searchParams = new URLSearchParams(baseQueryString);
    const cursor = options?.onCursorChange ? options.cursor : currentCursor;
    if (cursor) searchParams.set("cursor", cursor);
    const qs = searchParams.toString();
    return qs ? `${path}?${qs}` : path;
  }, [path, baseQueryString, currentCursor, options?.cursor, options?.onCursorChange]);

  useEffect(() => {
    setCursorStack([]);
    setCurrentCursor(undefined);
    setNextCursor(undefined);
  }, [path, baseQueryString]);

  const fetchData = useCallback(async (force = false) => {
    const sequence = ++requestSequence.current;
    setIsLoading(true);
    setError(null);
    try {
      const data = options?.fetcher
        ? await options.fetcher(buildUrl(), force)
        : await apiClient<PaginatedResponse<T>>(buildUrl());
      if (sequence !== requestSequence.current) return;
      setItems(data.items);
      setNextCursor(data.nextCursor ?? undefined);
    } catch (err) {
      if (sequence !== requestSequence.current) return;
      setError(apiErrorMessage(err));
    } finally {
      if (sequence === requestSequence.current) setIsLoading(false);
    }
  }, [buildUrl, options?.fetcher]);
  const refresh = useCallback((force = true) => fetchData(force), [fetchData]);

  useEffect(() => {
    void fetchData();
    return () => { requestSequence.current++; };
  }, [fetchData]);

  const goNext = useCallback(() => {
    if (!nextCursor) return;
    const cursor = options?.onCursorChange ? options.cursor : currentCursor;
    setCursorStack((prev) => [...prev, cursor ?? ""]);
    if (options?.onCursorChange) {
      options.onCursorChange(nextCursor);
    } else {
      setCurrentCursor(nextCursor);
    }
  }, [nextCursor, currentCursor, options]);

  const goPrev = useCallback(() => {
    setCursorStack((prev) => {
      const newStack = [...prev];
      const prevCursor = newStack.pop();
      const nextCursorValue = prevCursor || undefined;
      if (options?.onCursorChange) {
        options.onCursorChange(nextCursorValue);
      } else {
        setCurrentCursor(nextCursorValue);
      }
      return newStack;
    });
  }, [options]);

  return {
    items,
    isLoading,
    error,
    hasNext: !!nextCursor,
    hasPrev: cursorStack.length > 0,
    goNext,
    goPrev,
    refresh,
    requestUrl: buildUrl(),
  };
}
